import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, KeyRound, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validatePassword } from '@/features/users/utils/passwordValidation';
import { resetPassword, validatePasswordResetToken } from '../api';

const BRAND = {
  bone: '#F0F0F0',
  cobalt: '#5A1DEF',
  slate: '#404041',
} as const;

type TokenState = 'loading' | 'valid' | 'invalid';

export function PasswordResetPage() {
  const { t } = useTranslation('common');
  const { token } = useParams<{ token: string }>();
  const [tokenState, setTokenState] = useState<TokenState>('loading');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const passwordValidation = useMemo(() => validatePassword(password), [password]);
  const passwordsMatch = password.length > 0 && password === confirmation;

  useEffect(() => {
    let cancelled = false;

    async function validateToken() {
      if (!token) {
        setTokenState('invalid');
        return;
      }

      try {
        const result = await validatePasswordResetToken(token);
        if (!cancelled) setTokenState(result.valid === false ? 'invalid' : 'valid');
      } catch {
        if (!cancelled) setTokenState('invalid');
      }
    }

    validateToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token || !passwordValidation.isValid || !passwordsMatch) return;

    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    try {
      await resetPassword(token, password);
      setPassword('');
      setConfirmation('');
      setMessage(t('auth.passwordResetSuccess'));
    } catch (resetError) {
      if (resetError instanceof Error && resetError.message === 'password_already_used') {
        setError(t('auth.passwordAlreadyUsed'));
      } else {
        setError(t('auth.passwordResetError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: BRAND.bone }}
    >
      <div className="mb-8 text-center">
        <img
          src="https://storage.googleapis.com/atek-public-assets/brand/logos/atek-logo-main-dark-medium.png"
          alt="ATEK"
          className="h-16 w-auto mx-auto mb-3"
        />
        <p className="text-sm tracking-wide" style={{ color: BRAND.slate }}>
          {t('auth.tagline')}
        </p>
      </div>

      <Card className="w-full max-w-sm shadow-lg border-0">
        <CardContent className="pt-8 pb-8 px-8">
          <Link
            to="/login"
            className="mb-6 inline-flex items-center gap-2 text-sm hover:underline"
            style={{ color: BRAND.cobalt }}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('auth.backToSignIn')}
          </Link>

          {tokenState === 'loading' && (
            <p className="text-sm text-muted-foreground">{t('auth.checkingResetLink')}</p>
          )}

          {tokenState === 'invalid' && (
            <Alert variant="destructive">
              <AlertDescription>{t('auth.passwordResetInvalid')}</AlertDescription>
            </Alert>
          )}

          {tokenState === 'valid' && (
            <>
              <div className="mb-6">
                <div
                  className="mb-4 flex h-10 w-10 items-center justify-center rounded-full"
                  style={{ backgroundColor: 'rgba(90, 29, 239, 0.10)', color: BRAND.cobalt }}
                >
                  <KeyRound className="h-5 w-5" />
                </div>
                <h1 className="text-xl font-semibold text-foreground">
                  {t('auth.passwordResetTitle')}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('auth.passwordResetDescription')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {message && (
                  <Alert>
                    <AlertDescription>{message}</AlertDescription>
                  </Alert>
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('auth.confirmNewPassword')}</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="space-y-1.5 rounded border border-atek-grey-border bg-gray-50 p-3">
                  <PasswordRule met={passwordValidation.hasMinLength} label={t('auth.passwordRuleLength')} />
                  <PasswordRule met={passwordValidation.hasUppercase} label={t('auth.passwordRuleUppercase')} />
                  <PasswordRule met={passwordValidation.hasLowercase} label={t('auth.passwordRuleLowercase')} />
                  <PasswordRule met={passwordValidation.hasDigit} label={t('auth.passwordRuleDigit')} />
                  <PasswordRule met={passwordValidation.hasSpecialChar} label={t('auth.passwordRuleSpecial')} />
                  <PasswordRule met={passwordsMatch} label={t('auth.passwordRuleMatch')} />
                </div>

                <Button
                  type="submit"
                  className="w-full text-white font-medium"
                  style={{ backgroundColor: BRAND.cobalt }}
                  disabled={isSubmitting || !passwordValidation.isValid || !passwordsMatch}
                >
                  {isSubmitting ? t('auth.updatingPassword') : t('auth.updatePassword')}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PasswordRule({ met, label }: { met: boolean; label: string }) {
  const Icon = met ? Check : X;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className={`h-3.5 w-3.5 ${met ? 'text-green-600' : 'text-gray-400'}`} />
      <span>{label}</span>
    </div>
  );
}
